import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import GlassButton from '../UI/GlassButton';
import DynamicHeader from '../Header/DynamicHeader';
import axios from 'axios';
import './Notifications.scss';
import { photoVariant } from "../../utils/imageUrl";

/* ==== timeAgo (uk) ==== */
function timeAgo(ts) {
  const now = Date.now();
  let diff = Math.max(0, now - Number(ts || 0));
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec} с тому`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} хв тому`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs} год тому`;
  const d = Math.floor(hrs / 24);
  if (d === 1) return 'вчора';
  if (d < 7) return `${d} дн тому`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} тиж тому`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${m} міс тому`;
  const y = Math.floor(d / 365);
  return `${y} р тому`;
}

/* ==== text clamp helpers ==== */
function normalizeText(v) {
  return String(v ?? '')
    .replace(/\s+/g, ' ')
    .replace(/\u00A0/g, ' ')
    .trim();
}
function ellipsize(v, max = 96) {
  const s = normalizeText(v);
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

/* ==== local cache ==== */
const LS_KEY = 'cvanto:notifs:v1';
const SESSION_KEY = 'cvanto:notifs:cursor:v1';

/**
 * HARD TTL: allow showing cached list for a long time (smooth UX),
 * but we still revalidate on mount and on "load more".
 */
const HARD_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * SOFT TTL: "fresh enough" window; within this we can skip aggressive UI resets.
 * We still revalidate, but we avoid replacing list if nothing new arrived.
 */
const SOFT_TTL_MS = 2 * 60 * 1000;

function getLatestSignature(items) {
  if (!Array.isArray(items) || items.length === 0) return { latestId: null, latestCreatedAt: null };
  const first = items[0] || {};
  return {
    latestId: first.id ? String(first.id) : null,
    latestCreatedAt: first.createdAt ? Number(first.createdAt) : null,
  };
}

function isSameLatest(a, b) {
  if (!a || !b) return false;
  if (a.latestId && b.latestId) return String(a.latestId) === String(b.latestId);
  if (a.latestCreatedAt && b.latestCreatedAt) return Number(a.latestCreatedAt) === Number(b.latestCreatedAt);
  return false;
}

function loadFromCache(userId) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId) return null;
    if (Date.now() - (parsed.savedAt || 0) > HARD_TTL_MS) return null;
    return parsed.data || null;
  } catch { return null; }
}

function loadFromCacheStale(userId) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId) return null;
    if (Date.now() - (parsed.savedAt || 0) > HARD_TTL_MS) return null;

    const isFresh = Date.now() - (parsed.savedAt || 0) <= SOFT_TTL_MS;
    return { data: parsed.data || null, meta: parsed.meta || null, savedAt: parsed.savedAt || 0, isFresh };
  } catch {
    return null;
  }
}

function saveToCache(userId, data) {
  try {
    const items = data?.items;
    const meta = getLatestSignature(items);
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({ userId, savedAt: Date.now(), data, meta })
    );
  } catch {}
}

function saveCursor(cursor) {
  try { sessionStorage.setItem(SESSION_KEY, cursor || ''); } catch {}
}
function loadCursor() {
  try { const v = sessionStorage.getItem(SESSION_KEY); return v || null; } catch { return null; }
}

/* ==== image prefetch ==== */
const prefetched = new Set();
function prefetchImage(src) {
  if (!src || prefetched.has(src)) return;
  const img = new Image();
  img.src = src;
  prefetched.add(src);
}

/* ==== date helpers ==== */
function startOfDay(ts) {
  const d = new Date(Number(ts || 0));
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
function labelForDay(ts) {
  const d = new Date(Number(ts || 0));
  const today = new Date();
  const sod = startOfDay(d);
  const todaySod = startOfDay(today);
  const diffDays = Math.round((todaySod - sod) / 86400000);
  if (diffDays === 0) return 'Сьогодні';
  if (diffDays === 1) return 'Вчора';
  return d.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long' });
}
function keyForDay(ts) {
  const d = new Date(Number(ts || 0));
  return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
}

/* ==== moderation text helpers ==== */
function composeETA(minMins = 5, maxHours = 6) {
  const mins = Math.max(1, Number(minMins || 0));
  const hours = Math.max(1, Number(maxHours || 0));
  if (mins <= 10 && hours <= 6) return 'від кількох хвилин до кількох годин';
  return `від ~${mins} хв до ~${hours} год`;
}

const REASON_LABELS = {
  spam: 'Спам',
  scam: 'Шахрайство',
  hate: 'Мова ворожнечі',
  nudity: 'Непристойний/18+',
  violence: 'Насильство/жорстокість',
  illegal: 'Незаконний контент',
  misinformation: 'Дезінформація',
  other: 'Інше'
};

function labelForReasonKey(k) {
  return REASON_LABELS[String(k || '').toLowerCase()] || String(k || '').toUpperCase();
}

function summarizeReasonsFromCounts(reasonsCount = {}, max = 2) {
  const pairs = Object.entries(reasonsCount || {})
    .filter(([k, v]) => !!k && typeof v === 'number')
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([k]) => labelForReasonKey(k));
  if (!pairs.length) return null;
  if (pairs.length === 1) return pairs[0];
  return `${pairs[0]} та ${pairs[1]}`;
}

function summarizeReasons(topReasons = [], max = 2) {
  if (!Array.isArray(topReasons) || topReasons.length === 0) return null;
  const pick = topReasons
    .slice(0, max)
    .map(r => r?.label || labelForReasonKey(r?.key))
    .filter(Boolean);
  if (!pick.length) return null;
  if (pick.length === 1) return pick[0];
  return `${pick[0]} та ${pick[1]}`;
}

/* ==== non-navigable types ==== */
const DISABLE_NAV_TYPES = new Set([
  'moderation',
  'mod_user_under_review',
  'mod_post_under_review',
  'mod_listing_under_review',
  'mod_headline_under_review',
  'mod_content_hidden',
  'mod_auto_hidden',
  'mod_restored',
  'mod_removed',
  'user_frozen',
  'user_restored',
  'user_warned',
  'user_removed'
]);

export default function Notifications() {
  const { userId, isLoggedIn } = useAuth();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const listRef = useRef(null);

  const canLoad = isLoggedIn && userId;

  useEffect(() => {
    if (!canLoad) return;

    const cached = loadFromCacheStale(userId);
    const cachedCursor = loadCursor();

    if (cached?.data && Array.isArray(cached.data.items)) {
      setItems(cached.data.items);
      setNextCursor(cachedCursor);
      setLoading(false);
      cached.data.items.forEach(n => {
        const d = n.data || {};
        prefetchImage(
          d.actorAvatar || d.followerAvatar || d.birthdayUser?.avatar || d.postThumb
        );
      });
    }
  }, [canLoad, userId]);

  const persist = (itemsArr, cursor) => {
    saveToCache(userId, { items: itemsArr });
    saveCursor(cursor || '');
  };

  const dedupeById = (arr) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    const out = [];
    for (const it of arr) {
      const id = it?.id ? String(it.id) : null;
      if (!id) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(it);
    }
    return out;
  };

  const getAuthHeaders = async () => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('No access token');
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const load = async (cursor = null) => {
    if (!canLoad) return;

    try {
      setLoading(true);

      const cached = loadFromCacheStale(userId);
      const cachedItems = cached?.data?.items;
      const cachedMeta = cached?.meta || getLatestSignature(cachedItems);

      const headers = await getAuthHeaders();

      const params = {
        limit: 20,
      };
      if (cursor) params.cursor = cursor;

      const res = await axios.get('/api/notifications', {
        params,
        headers,
      });

      const data = res.data || {};

      (data.items || []).forEach(n => {
        const d = n.data || {};
        prefetchImage(
          d.actorAvatar || d.followerAvatar || d.birthdayUser?.avatar || d.postThumb
        );
      });

      const newItems = Array.isArray(data.items) ? data.items : [];
      const newMeta = getLatestSignature(newItems);

      if (!cursor) {
        const currentList = Array.isArray(items) && items.length ? items : (Array.isArray(cachedItems) ? cachedItems : []);
        const currentMeta = getLatestSignature(currentList);

        const noNew =
          (currentMeta.latestId || currentMeta.latestCreatedAt) &&
          isSameLatest(currentMeta, newMeta);

        if (noNew && currentList.length) {
          setNextCursor(data.nextCursor || null);
          persist(currentList, data.nextCursor || null);
        } else {
          setItems(newItems);
          setNextCursor(data.nextCursor || null);
          persist(newItems, data.nextCursor || null);
        }
      } else {
        setItems(prev => {
          const merged = dedupeById([...(Array.isArray(prev) ? prev : []), ...newItems]);
          persist(merged, data.nextCursor || null);
          return merged;
        });
        setNextCursor(data.nextCursor || null);
      }

      setError(null);
    } catch (e) {
      console.error('Notifications load error', e);
      setError('Не вдалося завантажити сповіщення');
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    if (!canLoad) return;

    try {
      setMarking(true);
      const headers = await getAuthHeaders();

      await axios.post(
        '/api/notifications/mark-all-read',
        {},
        { headers }
      );
    } catch (e) {
      console.warn('mark-all-read warn', e?.message);
    } finally {
      setMarking(false);
    }
  };

  useEffect(() => {
    if (!canLoad) return;
    (async () => {
      await load(null);
      await markAllRead();
    })();
  }, [canLoad, userId]);

  const onCardHover = (n) => {
    const d = n.data || {};
    prefetchImage(d.postThumb || d.actorAvatar);
  };

  const renderText = (n) => {
    const data = n.data || {};
    const who =
      data.actorName ||
      data.followerName ||
      data.actorUsername ||
      data.followerUsername ||
      data.birthdayUser?.username ||
      'Користувач';

    const postTitleRaw = data.postTitle;
    const commentTextRaw = data.commentText;

    const postTitle = postTitleRaw ? ellipsize(postTitleRaw, 92) : null;
    const commentText = commentTextRaw ? ellipsize(commentTextRaw, 120) : null;

    switch (n.type) {
      case 'like':
        return (
          <>
            <strong>{who}</strong> вподобав {postTitle ? <>«{postTitle}»</> : (data.contentType === 'ad' ? 'ваше оголошення' : 'ваш пост')}.
          </>
        );
      case 'comment':
        return (
          <>
            <strong>{who}</strong> залишив коментар{postTitle ? <> до «{postTitle}»</> : ''}{commentText ? <>: <span className="notif__quote">{commentText}</span></> : ''}.
          </>
        );
      case 'mention':
        return (
          <>
            <strong>{who}</strong> згадав(ла) вас у коментарі{postTitle ? <> до «{postTitle}»</> : ''}.
          </>
        );
      case 'follow':
        return <>Користувач <strong>{who}</strong> почав(ла) за вами стежити.</>;
      case 'birthday':
        return (
          <>
            <strong>{data.birthdayUser?.username || 'Користувач'}</strong> святкує день народження сьогодні!
          </>
        );
      case 'category': {
        const cat = data.category || 'категорії';
        const profileId = data.actorId || null;
        const safeTitle = data.postTitle ? ellipsize(data.postTitle, 92) : null;
        return (
          <>
            {profileId ? (
              <>
                Користувач{' '}
                <Link to={`/profile/${encodeURIComponent(profileId)}`} onClick={e => e.stopPropagation()}>
                  <strong>{who}</strong>
                </Link>{' '}
                створив оголошення{safeTitle ? <> «{safeTitle}»</> : ''} у категорії <strong>«{cat}»</strong>.
              </>
            ) : (
              <>
                У категорії <strong>«{cat}»</strong> нове оголошення{safeTitle ? <> — «{safeTitle}»</> : ''}.
              </>
            )}
          </>
        );
      }
      case 'moderation': {
        const reason = data.reasonText || null;
        const pt = data.postTitle
          ? ` «${ellipsize(data.postTitle, 92)}»`
          : (data.contentType === 'ad' ? ' ваше оголошення' : ' ваш контент');
        return (
          <>
            Модератор <strong>{data.moderatorName || data.actorName || 'Модератор'}</strong>
            {' '}видалив{pt}.{' '}
            {reason ? <>Причина: <em>{reason}</em>.</> : <>Причина не вказана.</>}
          </>
        );
      }
      case 'mod_user_under_review': {
        const u = data.username || 'користувач';
        const top = summarizeReasons(data.topReasons, 2);
        const eta = composeETA(data.etaMin ?? 5, data.etaMaxHours ?? 6);
        return (
          <>
            Шановний(на) <strong>{u}</strong>, у зв’язку з <strong>численними скаргами</strong> на ваш акаунт
            {top ? <> (зокрема: <em>{top}</em>)</> : null}, ми <strong>тимчасово відправили</strong> акаунт на модерацію.
            Якщо порушень <strong>не</strong> виявимо — ви знову зможете активно користуватись соцмережею.
            У разі виявлення порушень — будуть попередження, а надалі можлива деактивація. Орієнтовні строки розгляду — {eta}.
          </>
        );
      }
      case 'mod_listing_under_review': {
        const u = data.username || 'користувач';
        const title = data.postTitle ? `«${ellipsize(data.postTitle, 92)}»` : 'ваше оголошення';
        const top = summarizeReasons(data.topReasons, 2);
        const eta = composeETA(data.etaMin ?? 5, data.etaMaxHours ?? 6);
        return (
          <>
            Шановний(на) <strong>{u}</strong>, у зв’язку з <strong>численними скаргами</strong> на {title}
            {top ? <> (зокрема: <em>{top}</em>)</> : null}, ми <strong>тимчасово приховали</strong> його та відправили на модерацію.
            Ми повідомимо про стан публікації — {eta}.
          </>
        );
      }
      case 'mod_post_under_review': {
        const u = data.username || 'користувач';
        const title = data.postTitle ? `«${ellipsize(data.postTitle, 92)}»` : 'вашу публікацію';
        const top = summarizeReasons(data.topReasons, 2);
        const eta = composeETA(data.etaMin ?? 5, data.etaMaxHours ?? 6);
        return (
          <>
            Шановний(на) <strong>{u}</strong>, у зв’язку з <strong>численними скаргами</strong> на {title}
            {top ? <> (зокрема: <em>{top}</em>)</> : null}, ми <strong>тимчасово приховали</strong> її та відправили на модерацію.
            Сповіщення про результат надійде {eta}.
          </>
        );
      }
      case 'mod_headline_under_review': {
        const u = data.username || 'користувач';
        const title = data.headlineText ? `«${ellipsize(data.headlineText, 92)}»` : 'ваш Headline';
        const top = summarizeReasons(data.topReasons, 2);
        const eta = composeETA(data.etaMin ?? 5, data.etaMaxHours ?? 6);
        return (
          <>
            Шановний(на) <strong>{u}</strong>, у зв’язку з <strong>численними скаргами</strong> на {title}
            {top ? <> (зокрема: <em>{top}</em>)</> : null}, ми <strong>тимчасово приховали</strong> його та відправили на модерацію.
            Орієнтовний час перевірки — {eta}.
          </>
        );
      }
      case 'mod_content_hidden': {
        const u = data.username || 'користувач';
        const type = data.contentType || 'контент';
        const title =
          (data.postTitle && `«${ellipsize(data.postTitle, 92)}»`) ||
          (data.headlineText && `«${ellipsize(data.headlineText, 92)}»`) ||
          (type === 'ad' || type === 'listing' ? 'ваше оголошення' :
           type === 'post' ? 'вашу публікацію' :
           type === 'headline' ? 'ваш Headline' : 'ваш контент');
        const top = summarizeReasons(data.topReasons, 2);
        const eta = composeETA(data.etaMin ?? 5, data.etaMaxHours ?? 6);
        return (
          <>
            Шановний(на) <strong>{u}</strong>, у зв’язку з <strong>численними скаргами</strong> на {title}
            {top ? <> (зокрема: <em>{top}</em>)</> : null}, ми <strong>тимчасово приховали</strong> його та відправили на модерацію.
            Повідомимо про результат {eta}.
          </>
        );
      }
      case 'mod_auto_hidden': {
        const kind = data.contentType === 'ad' ? 'оголошення'
                    : data.contentType === 'headline' ? 'Headline'
                    : data.contentType === 'group' ? 'спільноту'
                    : 'публікацію';
        const title = data.postTitle ? `«${ellipsize(data.postTitle, 92)}»` : `вашу ${kind}`;
        const top = summarizeReasonsFromCounts(data.reasonsCount || {}, 2);
        const eta = composeETA(5, 6);
        return (
          <>
            Шановний(на) користувачу, у зв’язку з <strong>численними скаргами</strong> на {title}
            {top ? <> (зокрема: <em>{top}</em>)</> : null}, ми <strong>тимчасово приховали</strong> його та відправили на модерацію.
            Ми повідомимо про стан публікації — {eta}.
          </>
        );
      }
      case 'mod_restored': {
        const kind = data.contentType === 'ad' ? 'оголошення' : 'публікацію';
        const title = data.postTitle ? `«${ellipsize(data.postTitle, 92)}»` : `вашу ${kind}`;
        return (
          <>
            {title} <strong>повернуто у видимість</strong>. Порушень не виявлено. Дякуємо за очікування!
          </>
        );
      }
      case 'mod_removed': {
        const kind = data.contentType === 'ad' ? 'оголошення' : (data.contentType === 'headline' ? 'Headline' : 'публікацію');
        const title = data.postTitle ? `«${ellipsize(data.postTitle, 92)}»` : `вашу ${kind}`;
        const reason = data.moderatorNote || data.reasonCode || null;
        return (
          <>
            {title} <strong>видалено модерацією</strong>. {reason ? <>Причина: <em>{reason}</em>.</> : <>Порушення правил спільноти.</>}
          </>
        );
      }
      case 'user_frozen': {
        const top = summarizeReasonsFromCounts(data.reasonsCount || {}, 2);
        const eta = composeETA(5, 6);
        return (
          <>
            Ваш акаунт <strong>відправлено на модерацію</strong>{top ? <> (найчастіші скарги: <em>{top}</em>)</> : null}.
            Поки триває перевірка, взаємодія обмежена, профіль невидимий. Результат перевірки — {eta}.
          </>
        );
      }
      case 'user_restored':
        return <>Акаунт <strong>відновлено</strong>. Порушень не виявлено — обмеження знято.</>;
      case 'user_warned': {
        const note = data?.note ? String(data.note) : null;
        return (
          <>
            <strong>Попередження щодо акаунта.</strong> {note ? <>{note} </> : null}
            Повторні порушення можуть призвести до обмежень або видалення.
          </>
        );
      }
      case 'user_removed': {
        const note = data?.note ? String(data.note) : null;
        return (
          <>
            Акаунт <strong>видалено модерацією</strong>. {note ? <>Причина: <em>{note}</em>.</> : <>Порушення правил спільноти.</>}
          </>
        );
      }
      default:
        return (
          <>
            {n.title || 'Сповіщення'}
            {n.body ? <> — <em>{n.body}</em></> : null}
          </>
        );
    }
  };

  const onCardClick = (n) => {
    const data = n.data || {};

    if (n.type === 'moderation') return;
    if (DISABLE_NAV_TYPES.has(n.type)) return;

    const contentType = data.contentType || 'post';
    const isPostInteraction =
      n.type === 'like' || n.type === 'comment' || n.type === 'mention';

    if (isPostInteraction && data.postId && contentType !== 'ad') {
      const ownerId =
        data.ownerId ||
        data.profileId ||
        n.userId ||
        userId;
      const url = `/profile/${encodeURIComponent(ownerId)}?tab=posts&item=${encodeURIComponent(data.postId)}`;
      return navigate(url);
    }

    if (n.type === 'birthday' && data.birthdayUser?.id) {
      return navigate(`/profile/${encodeURIComponent(data.birthdayUser.id)}`);
    }

    if (n.type === 'category' && data.postId) {
      if (data.contentType === 'ad') {
        return navigate(`/listing/${encodeURIComponent(data.postId)}`);
      }
      return navigate(`/post/${encodeURIComponent(data.postId)}`);
    }

    if (n.url) return navigate(n.url);
    if (data.contentUrl) return navigate(data.contentUrl);

    const ownerId = data.ownerId || n.userId || userId;
    const postId = data.postId;
    const type = data.contentType === 'ad' ? 'listings' : 'posts';
    if (ownerId && postId) {
      const url = `/profile/${encodeURIComponent(ownerId)}?tab=${type}&item=${encodeURIComponent(postId)}`;
      return navigate(url);
    }

    navigate(`/profile/${encodeURIComponent(n.userId || userId)}`);
  };

  const empty = !loading && (!items || items.length === 0);

  const dayGroups = useMemo(() => {
    if (!items?.length) return [];
    const map = new Map();
    items.forEach(n => {
      const key = keyForDay(n.createdAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    });
    const orderedKeys = Array.from(map.keys()).sort((a, b) => (a < b ? 1 : -1));
    return orderedKeys.map(k => {
      const any = map.get(k)[0];
      return { key: k, label: labelForDay(any.createdAt), items: map.get(k) };
    });
  }, [items]);

  const GROUP_BASE_DELAY = 30;
  const ITEM_STEP = 24;

  return (
    <>
      <DynamicHeader
        title="Сповіщення"
        variant="solid"
        position="fixed"
        size="compact"
        showBack
        showMenu={false}
        withSpacer
      />

      <div className="notifications notifications--has-local-header">
        {error && <div className="notifications__error">{error}</div>}

        {loading && !items.length ? (
          <ul className="notifications__list" aria-busy="true" ref={listRef}>
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={`sk-${i}`}>
                <div className="notif skeleton">
                  <div className="notif__main">
                    <div className="notif__avatar skeleton-box" />
                    <div className="notif__text">
                      <div className="skeleton-line w80" />
                      <div className="skeleton-line w40 mt6" />
                    </div>
                    <div className="notif__thumb skeleton-box sm-hide" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : empty ? (
          <div className="notifications__empty">Сповіщень поки немає.</div>
        ) : (
          <div className="notifications__days" ref={listRef}>
            {dayGroups.map((group, gIdx) => (
              <section
                key={group.key}
                className="day-group tg-appear"
                style={{ ['--stagger']: `${gIdx * GROUP_BASE_DELAY}ms` }}
              >
                <div className="day-group__header tg-appear" style={{ ['--stagger']: `${gIdx * GROUP_BASE_DELAY + 10}ms` }}>
                  <span className="day-group__title">{group.label}</span>
                </div>

                <ul className="day-group__list">
                  {group.items.map((n, i) => {
                    const data = n.data || {};
                    const avatar  = data.actorAvatar || data.followerAvatar || data.birthdayUser?.avatar || null;
                    const profileId =
                      data.actorId || data.followerId || data.birthdayUser?.id || null;
                    const profileUrl = profileId ? `/profile/${encodeURIComponent(profileId)}` : null;
                    const thumb = data.postThumb || null;

                    const isDisabled = DISABLE_NAV_TYPES.has(n.type) || n.type === 'moderation';
                    const commonHandlers = isDisabled
                      ? {}
                      : {
                          onClick: (e) => { e.preventDefault(); onCardClick(n); },
                          onKeyDown: (e) => { if (e.key === 'Enter') onCardClick(n); },
                        };

                    const delay = gIdx * GROUP_BASE_DELAY + (i + 1) * ITEM_STEP;

                    return (
                      <li key={n.id} className="day-group__item tg-appear" style={{ ['--stagger']: `${delay}ms` }}>
                        <div
                          className={`notif notif--${n.type} tg-appear`}
                          style={{ ['--stagger']: `${delay + 8}ms` }}
                          {...commonHandlers}
                          role={isDisabled ? undefined : 'button'}
                          tabIndex={isDisabled ? -1 : 0}
                          onMouseEnter={() => onCardHover(n)}
                        >
                          <div className="notif__main">
                            {avatar ? (
                              profileUrl ? (
                                <Link
                                  to={profileUrl}
                                  className="notif__avatar tg-appear"
                                  style={{ ['--stagger']: `${delay + 16}ms` }}
                                  title="Профіль"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <img src={avatar} alt="user" />
                                </Link>
                              ) : (
                                <div
                                  className="notif__avatar tg-appear"
                                  style={{ ['--stagger']: `${delay + 16}ms` }}
                                >
                                  <img src={avatar} alt="user" />
                                </div>
                              )
                            ) : (
                              <div
                                className="notif__avatar placeholder tg-appear"
                                style={{ ['--stagger']: `${delay + 16}ms` }}
                              />
                            )}

                            <div
                              className="notif__text tg-appear"
                              style={{ ['--stagger']: `${delay + 24}ms` }}
                            >
                              <div className="notif__line">{renderText(n)}</div>
                              <div className="notif__meta">
                                <span className="notif__time">{timeAgo(n.createdAt)}</span>
                              </div>
                            </div>

                            {thumb ? (
                              <div
                                className="notif__thumb tg-appear"
                                style={{ ['--stagger']: `${delay + 28}ms` }}
                                aria-hidden
                              >
                                <img src={photoVariant(thumb, "xs")} alt="" loading="lazy" decoding="async" />
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}

            {nextCursor && (
              <div className="notifications__more tg-appear" style={{ ['--stagger']: `${(dayGroups.length + 1) * GROUP_BASE_DELAY}ms` }}>
                <GlassButton
                  size="lg"
                  variant="primary"
                  onClick={() => load(nextCursor)}
                  ariaLabel="Завантажити ще сповіщення"
                  active={false}
                  disabled={loading}
                >
                  {loading ? 'Завантажую…' : 'Завантажити ще'}
                </GlassButton>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}